import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, Waves, Scale, Tags, Sparkles, ChevronRight, ArrowLeft, Loader2 } from "lucide-react";
import { TOPICS, LEVELS } from "@/lib/topics";
import { useClient } from "@/context/ClientContext";

const ICONS = { TrendingUp, Waves, Scale, Tags, Sparkles };
const LEVEL_STYLE = {
  new: { label: "Just getting started", bg: "bg-amber-50", text: "text-amber-700", dot: "#F59E0B" },
  some: { label: "Knows the basics", bg: "bg-sky-50", text: "text-sky-700", dot: "#29ABE2" },
  confident: { label: "Confident with the numbers", bg: "bg-emerald-50", text: "text-emerald-700", dot: "#10B981" },
};

const container = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };
const item = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 260, damping: 22 } } };

export default function ClientHome() {
  const navigate = useNavigate();
  const { client, loading, progress, clientId } = useClient();

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-sky-400 animate-spin" /></div>;
  if (!client) return <div className="text-center py-24 font-display text-xl font-bold text-slate-600">Client not found.</div>;

  const lvl = LEVEL_STYLE[client.knowledge_level] || LEVEL_STYLE.some;
  const topicPct = (t) => {
    const cells = LEVELS.map((l) => progress[t.id]?.[l.id]);
    const done = cells.filter((c) => c?.completed).length;
    return Math.round((done / LEVELS.length) * 100);
  };

  return (
    <div className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-sky-200/40 blur-3xl floaty" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-24">
        <button
          data-testid="back-to-clients-btn"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 text-slate-400 hover:text-slate-700 font-bold text-sm mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> All clients
        </button>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold mb-4 ${lvl.bg} ${lvl.text}`}>
            <span className="w-2 h-2 rounded-full" style={{ background: lvl.dot }} />
            {lvl.label}{client.assessment_score != null ? ` · ${client.assessment_score}%` : ""}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#005580] tracking-tight leading-[1.05]">
            {client.business_name || client.name}
          </h1>
          <p className="mt-3 text-slate-500 text-base sm:text-lg font-semibold max-w-xl">
            Learnings tailored to {client.name.split(" ")[0]}'s level. Pick a topic to begin.
          </p>
        </motion.div>

        <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#005580] mb-1">Choose a learning</h2>
        <p className="text-slate-400 font-semibold mb-6">Each topic has three levels to climb.</p>

        <motion.div variants={container} initial="hidden" animate="show"
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {TOPICS.map((t) => {
            const Icon = ICONS[t.icon];
            const pct = topicPct(t);
            return (
              <motion.button
                key={t.id}
                variants={item}
                whileHover={{ scale: 1.03, y: -4 }}
                whileTap={{ scale: 0.97 }}
                data-testid={`topic-card-${t.id}`}
                onClick={() => navigate(`/clients/${clientId}/topic/${t.id}`)}
                className="group relative text-left rounded-3xl p-6 border-2 bg-white shadow-sm hover:shadow-xl transition-shadow"
                style={{ borderColor: t.tint }}
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 border-b-4" style={{ background: t.tint, borderColor: t.color }}>
                  <Icon className="w-7 h-7" strokeWidth={2.5} style={{ color: t.deep }} />
                </div>
                <h3 className="font-display font-bold text-xl text-slate-800 mb-1">{t.name}</h3>
                <p className="text-slate-400 text-sm font-semibold mb-5 min-h-[40px]">{t.tagline}</p>
                <div className="flex items-center justify-between">
                  <div className="flex-1 mr-3">
                    <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full relative" style={{ width: `${pct}%`, background: t.color }}>
                        <div className="absolute inset-0 shimmer" />
                      </div>
                    </div>
                    <span className="text-[11px] font-bold text-slate-400 mt-1 inline-block">{pct}% complete</span>
                  </div>
                  <span className="w-9 h-9 rounded-full flex items-center justify-center text-white group-hover:translate-x-0.5 transition-transform" style={{ background: t.color }}>
                    <ChevronRight className="w-5 h-5" strokeWidth={3} />
                  </span>
                </div>
              </motion.button>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
}
